/**
 * familienstandAbleiten.ts — der Familienstand aus den Partner-Beziehungen
 * einer Person (Themen 01.10.2026, T3).
 *
 * SPIEGEL von `apps/api/src/familienstandAbleiten.ts` — dieselbe Regel,
 * dieselben Testfälle (familienstandAbleiten.test.ts). Wer hier eine Regel
 * ändert, ändert sie dort mit. Genutzt von der Kontaktanlage
 * (PrivateRelationsSection) und der Akte (Popup „Ehepartner erfassen").
 *
 * Regeln in dieser Reihenfolge:
 *   1. Genau EIN lebender Ehepartner        → „Verheiratet"
 *      Mehr als einer                       → nichts (im Bestand fast immer
 *                                              eine falsch gesetzte Kante)
 *   2. Ehepartner da, aber alle verstorben  → „Verwitwet"
 *   3. Lebender Lebenspartner               → „Zusammenlebend"
 *   4. Ex-Ehemann / Ex-Ehefrau / Ex-Partner / Ex-Partnerin / Ex-Partner(in)
 *                                           → „Geschieden"
 *
 * „Ex-Partner" ist bei uns die Beziehung nach einer Scheidung (User
 * 02.10.2026: „wenn geschieden, dann Ex-Partner"). „Ex-Lebenspartner(in)"
 * und „Partner(in)" ändern weiterhin nichts — eine beendete Lebens-
 * gemeinschaft ist keine Scheidung.
 *
 * Gesetzt wird NUR, wenn das Feld leer oder „Ledig" ist. Ein bewusst
 * gepflegter Wert („getrennt lebend", „Geschieden" trotz neuer Ehe …) bleibt
 * stehen — die Ableitung ergänzt, sie korrigiert nicht.
 *
 * Richtung: Das Label einer Kante sagt „was dest für src ist". Die Partner-
 * Arten sind symmetrisch — ob A „Ehemann" von B oder B „Ehefrau" von A ist,
 * beide Zeilen sagen „A und B sind verheiratet". Der Aufrufer liefert deshalb
 * die Kanten BEIDER Richtungen, je mit dem Gegenüber als `partnerId`; doppelte
 * Zeilen desselben Paares zählen über die id nur einmal.
 */
/**
 * Ausschnitt von `artVonLabel` (apps/api/src/verwandtschaftLabels.ts): nur die
 * beiden Arten, auf die es beim Familienstand ankommt. Die Listen müssen mit
 * `ARTEN.partner` / `ARTEN.ex_partner` dort übereinstimmen.
 */
const ARTEN = {
  partner: [
    'Ehemann', 'Ehefrau', 'Ehepartner', 'Ehepartner(in)',
    'Lebenspartner', 'Lebenspartnerin', 'Lebenspartner(in)',
  ],
  ex_partner: [
    'Ex-Partner', 'Ex-Partnerin', 'Ex-Partner(in)',
    'Ex-Ehemann', 'Ex-Ehefrau', 'Ex-Lebenspartner', 'Ex-Lebenspartnerin',
  ],
} as const;

export function artVonLabel(label: string | null | undefined): 'partner' | 'ex_partner' | null {
  const l = String(label ?? '').trim();
  if ((ARTEN.partner as readonly string[]).includes(l)) return 'partner';
  if ((ARTEN.ex_partner as readonly string[]).includes(l)) return 'ex_partner';
  return null;
}

export type PartnerArt = 'ehe' | 'lebenspartner' | 'ex';

/** Eine Partner-Kante aus Sicht der Person, deren Familienstand gesucht ist. */
export type FamilienstandKante = {
  /** Das Gegenüber — zählt mehrfache Zeilen desselben Paares nur einmal. */
  partnerId: number | string;
  /** Gespeichertes Label der Kante (Richtung egal, siehe oben). */
  label: string | null | undefined;
  /** Gegenüber ist verstorben (`Contact.deceased`). */
  partnerVerstorben: boolean;
};

/** Ex-Beziehungen, die „Geschieden" bedeuten — ohne Ex-Lebenspartner(in). */
const EX_GESCHIEDEN = new Set(['Ex-Ehemann', 'Ex-Ehefrau', 'Ex-Partner', 'Ex-Partnerin', 'Ex-Partner(in)']);

/**
 * Partner-Art eines Labels über {@link artVonLabel} — oder `null`, wenn das
 * Label für den Familienstand nichts aussagt.
 */
export function partnerArtVonLabel(label: string | null | undefined): PartnerArt | null {
  const l = String(label ?? '').trim();
  const art = artVonLabel(l);
  if (art === 'partner') return l.startsWith('Ehe') ? 'ehe' : 'lebenspartner';
  if (art === 'ex_partner' && EX_GESCHIEDEN.has(l)) return 'ex';
  return null;
}

/** Darf die Ableitung den gespeicherten Wert überschreiben? Nur leer oder „Ledig". */
export function familienstandUeberschreibbar(aktuell: string | null | undefined): boolean {
  const w = String(aktuell ?? '').trim().toLowerCase();
  return w === '' || w === 'ledig';
}

/**
 * Was die Partner-Kanten über den Familienstand sagen — ohne Rücksicht auf den
 * gespeicherten Wert. `null` = nichts Eindeutiges.
 */
export function familienstandAusKanten(kanten: readonly FamilienstandKante[]): string | null {
  const ehe = new Map<string, boolean>();
  const lebenspartner = new Map<string, boolean>();
  let ex = false;
  for (const k of kanten) {
    const art = partnerArtVonLabel(k.label);
    if (!art) continue;
    const id = String(k.partnerId);
    if (art === 'ehe') ehe.set(id, k.partnerVerstorben === true);
    else if (art === 'lebenspartner') lebenspartner.set(id, k.partnerVerstorben === true);
    else ex = true;
  }
  const lebendeEhe = [...ehe.values()].filter((tot) => !tot).length;
  if (lebendeEhe > 1) return null;
  if (lebendeEhe === 1) return 'Verheiratet';
  if (ehe.size > 0) return 'Verwitwet';
  if ([...lebenspartner.values()].some((tot) => !tot)) return 'Zusammenlebend';
  if (ex) return 'Geschieden';
  return null;
}

/**
 * Der neue Familienstand — oder `null`, wenn nichts zu schreiben ist (nichts
 * ableitbar, Feld bewusst gepflegt oder schon richtig).
 */
export function familienstandAbleiten(
  aktuell: string | null | undefined,
  kanten: readonly FamilienstandKante[],
): string | null {
  if (!familienstandUeberschreibbar(aktuell)) return null;
  const neu = familienstandAusKanten(kanten);
  if (!neu || neu === String(aktuell ?? '').trim()) return null;
  return neu;
}
